import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Organization } from "@/types/organization";

interface OrganizationStats {
  bookings: number;
  members: number;
  settings: number;
  parcelTypes: number;
  eventFlows: number;
  templates: number;
  pendingMemberships: number;
}

interface DeletionResult {
  success: boolean;
  error?: string;
  organization?: {
    id: string;
    name: string;
    subdomain: string;
  };
  deleted_counts?: {
    bookings: number;
    quotes: number;
    parcels: number;
    contacts: number;
    activity_logs: number;
    event_queue: number;
    orchestration_logs: number;
    payment_events: number;
    members: number;
    settings: number;
    parcel_types: number;
    event_flows: number;
    templates: number;
    pending_memberships: number;
  };
}

export const useOrganizationDeletion = () => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isFetchingStats, setIsFetchingStats] = useState(false);
  const { toast } = useToast();

  const fetchOrganizationStats = async (orgId: string): Promise<OrganizationStats> => {
    setIsFetchingStats(true);
    try {
      // Fetch all counts in parallel
      const [
        { count: bookingsCount },
        { count: membersCount },
        { count: settingsCount },
        { count: parcelTypesCount },
        { count: eventFlowsCount },
        { count: templatesCount },
        { count: pendingMembershipsCount },
      ] = await Promise.all([
        supabase.from('bookings').select('*', { count: 'exact', head: true }).eq('org_id', orgId),
        supabase.from('organization_user_roles').select('*', { count: 'exact', head: true }).eq('org_id', orgId),
        supabase.from('organization_settings').select('*', { count: 'exact', head: true }).eq('org_id', orgId),
        supabase.from('parcel_types').select('*', { count: 'exact', head: true }).eq('org_id', orgId),
        supabase.from('event_flows').select('*', { count: 'exact', head: true }).eq('org_id', orgId),
        supabase.from('communication_templates').select('*', { count: 'exact', head: true }).eq('org_id', orgId),
        supabase.from('pending_memberships').select('*', { count: 'exact', head: true }).eq('org_id', orgId),
      ]);

      return {
        bookings: bookingsCount || 0,
        members: membersCount || 0,
        settings: settingsCount || 0,
        parcelTypes: parcelTypesCount || 0,
        eventFlows: eventFlowsCount || 0,
        templates: templatesCount || 0,
        pendingMemberships: pendingMembershipsCount || 0,
      };
    } catch (error) {
      console.error('Error fetching organization stats:', error);
      toast({
        title: "Error",
        description: "Failed to fetch organization statistics",
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsFetchingStats(false);
    }
  };

  const deleteOrganization = async (org: Organization): Promise<DeletionResult> => {
    setIsDeleting(true);
    try {
      // Call the database function
      const { data, error } = await supabase.rpc('delete_organization_cascade', {
        p_org_id: org.id,
      });

      if (error) {
        throw error;
      }

      const result = data as unknown as DeletionResult;

      if (!result.success) {
        toast({
          title: "Deletion Failed",
          description: result.error || "Failed to delete organization",
          variant: "destructive",
        });
        return result;
      }

      // Success - show detailed toast
      const counts = result.deleted_counts;
      const totalRecords = counts 
        ? Object.values(counts).reduce((sum, count) => sum + count, 0)
        : 0;

      toast({
        title: "Organization Deleted",
        description: `Successfully deleted ${org.name} and ${totalRecords} related records`,
      });

      return result;
    } catch (error: any) {
      console.error('Error deleting organization:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete organization",
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    deleteOrganization,
    fetchOrganizationStats,
    isDeleting,
    isFetchingStats,
  };
};
